/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

import { RefObject, VNode } from "preact";
import { html } from "htm/preact";
import { useLayoutEffect, useRef } from "preact/hooks";
import { BlissSymbol } from "../components/BlissSymbol";
import { adaptivePaletteGlobals, changeEncodingContents, settingsSavedCount, wordBuildSignal } from "../state/GlobalData";
import { editMessage } from "../core/MessageEdit";
import { ContentEncodingType, SymbolEncodingType } from "../index.d";
import { COMBINE_MARKER_ID } from "../utils/SvgUtils";
import { generateGridStyle } from "../utils/GridUtils";
import { announceIfEnabled } from "../utils/SpeechUtils";
import { t } from "../i18n/I18n";
import "./ContentEncoding.scss";

export const INPUT_AREA_ID = "content-encoding-area";   // better way?

const isApplePlatform = navigator.platform.startsWith("Mac") || navigator.platform.startsWith("iPhone") || navigator.platform.startsWith("iPad");

/**
 * Returns the inputted value constrained to the `min` and `max` values.
 * The returned value will:
 *  - `min` if `value` was less than `min`
 *  - `max` if the `value` was greater than `max`
 *  - `value` if it fell within the `min` `max` range.
 *
 * @param {number} value - The value to evaluate
 * @param {number} min - The minimum value to be returned
 * @param {number} max - The maximum value to be returned
 * @returns {number} - The constrained value
 */
export function clamp (value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

type ContentEncodingProps = {
  id: string,
  options: ContentEncodingType
}

// The caret position given for the message around a word being built: no symbol in it has the
// caret, since the caret is in the word.
const NO_CARET = -2;

/*
 * Given an array of symbols and a caret position create the proper markup for
 * each symbol in the array:
 * - generate markup for each symbol,
 * - if the symbol is at the caret position, add caret styles to the markup,
 * - if the caret position is -1, and there are symbols in the array, add a
 *   special caret markup to indicate insertion is possible before the first
 *   symbol.
 * @param {ContentSignalDataType} symbols: Array of symbols and caret position.
 * @return {Array<VNode>} - Array of markup for the symbols
 */
export function generateMarkupArray (payloadArray: Array<SymbolEncodingType>, caretPos: number): Array<VNode> {
  // Read once here rather than per symbol.
  const markAiSuggestions = adaptivePaletteGlobals.config.markAiSuggestions;
  // NOTE:  if there are no payloads in the `payloadArray`, the map() function
  // immediately returns an empty array.  That is, the function passed to map()
  // will execute only if `payloadArray.length` is non-zero -- there is no need
  // to check for a length of zero within the mapping function.
  return payloadArray.map((payload, index) => {
    const isMarked = payload.isAiLabel && markAiSuggestions;
    // The caret before the first symbol, the caret on this symbol, or no caret.
    const caretClass = index === 0 && caretPos === -1 ? " insertionCaret" : index === caretPos ? " cursorCaret" : "";
    // A space has no symbol: an outlined gap, named for a screen reader.
    if (payload.isSpace) {
      return html`<div class=${`blissSymbol wordSpace${caretClass}`}><span>${t("wordSpace")}</span></div>`;
    }
    return html`
      <div class=${`blissSymbol${caretClass}`}>
        <${BlissSymbol} composition=${payload.composition} picture=${payload.picture} label=${payload.label} isPresentation="true" isMarked=${isMarked} />
      </div>
    `;
  });
}

/**
 * The word being built: a dashed box inside the message, with a tag naming it, then its parts,
 * between two combine markers when the marker is on. An empty word shows the caret alone.
 * @param {SymbolEncodingType[]} parts - The word's parts.
 * @param {number} caretPos - The caret's position in the parts.
 * @param {boolean} combine - Whether the combine marker is on.
 * @return {VNode}
 */
function newWordMarkup (parts: SymbolEncodingType[], caretPos: number, combine: boolean): VNode {
  // A function, so each end gets an element of its own.
  const marker = (): VNode | null => combine
    ? html`<div class="blissSymbol combineMarker">
        <${BlissSymbol} composition=${COMBINE_MARKER_ID} label=${t("combineMarker")} isPresentation="true" />
      </div>`
    : null;
  const partsMarkup = parts.length === 0
    ? html`<div class="blissSymbol insertionCaret emptyWord"></div>`
    : generateMarkupArray(parts, caretPos);
  return html`
    <div class="newWord">
      <span class="newWordTag">${t("newWordTag")}</span>
      ${marker()}${partsMarkup}${marker()}
    </div>
  `;
}

/**
 * The message as the input area draws it, with its caret. While a word is built, the input area
 * holds its parts, and the message is drawn around them, with the word where it will go: after
 * the message's caret. Shared by `ContentEncoding` and `MessagePreview`.
 * @return {Array<VNode>}
 */
export function messageMarkup (): Array<VNode> {
  const { payloads, caretPosition } = changeEncodingContents.value;
  const build = wordBuildSignal.value;
  if (!build) {
    return generateMarkupArray(payloads, caretPosition);
  }
  const splitAt = build.message.caretPosition + 1;
  return [
    ...generateMarkupArray(build.message.payloads.slice(0, splitAt), NO_CARET),
    newWordMarkup(payloads, caretPosition, build.combine),
    ...generateMarkupArray(build.message.payloads.slice(splitAt), NO_CARET)
  ];
}

/**
 * Scrolls the message area by as little as needed to show the caret with the symbol on each side
 * of it, or just the caret's bar when the three do not fit. The scroll padding keeps them clear of the
 * edge markers. Only the area scrolls: `scrollIntoView()` would scroll the page or a dialog too.
 * @param {HTMLElement} area - The scrolling message element.
 */
function showCaret (area: HTMLElement): void {
  const caret = area.querySelector(".cursorCaret, .insertionCaret");
  if (!caret) {
    return;
  }
  const padding = parseFloat(getComputedStyle(area).scrollPaddingLeft) || 0;
  const areaLeft = area.getBoundingClientRect().left + area.clientLeft;
  const start = areaLeft + padding;
  const end = areaLeft + area.clientWidth - padding;
  const rects = [caret.previousElementSibling, caret, caret.nextElementSibling]
    .filter((element): element is Element => element !== null)
    .map((element) => element.getBoundingClientRect());
  let left = Math.min(...rects.map((rect) => rect.left));
  let right = Math.max(...rects.map((rect) => rect.right));
  if (right - left > end - start) {
    const rect = caret.getBoundingClientRect();
    left = right = caret.classList.contains("cursorCaret") ? rect.right : rect.left;
  }
  if (left < start) {
    area.scrollLeft -= start - left;
  } else if (right > end) {
    area.scrollLeft += right - end;
  }
}

/**
 * Marks each edge of the message area that has symbols hidden past it, for the markers.
 * 1px of slack at the end for sub-pixel widths.
 * @param {HTMLElement} area - The scrolling message element.
 */
function markHiddenEdges (area: HTMLElement): void {
  area.toggleAttribute("data-hidden-start", area.scrollLeft > 0);
  area.toggleAttribute("data-hidden-end", area.scrollLeft + area.clientWidth < area.scrollWidth - 1);
}

/**
 * Scrolls to the caret, then updates the edge markers.
 * @param {HTMLElement} area - The scrolling message element.
 */
function followCaret (area: HTMLElement): void {
  showCaret(area);
  markHiddenEdges(area);
}

/**
 * Keeps the caret of a message area in view: after every draw, when the area changes size, and
 * when a picture in it loads. Scrolling by hand only updates the edge markers, so the area stays
 * where the user put it until the next change.
 * @return {RefObject<HTMLDivElement | null>} The ref for the scrolling message element.
 */
export function useCaretInView (): RefObject<HTMLDivElement | null> {
  const areaRef = useRef<HTMLDivElement>(null);

  // No dependency list: every edit, a language change and a settings save draw the area again.
  useLayoutEffect(() => {
    if (areaRef.current) {
      followCaret(areaRef.current);
    }
  });

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) {
      return;
    }
    const follow = (): void => followCaret(area);
    const mark = (): void => markHiddenEdges(area);
    // Observing starts with a call for the current size, which is no change to follow.
    let width = area.clientWidth;
    let height = area.clientHeight;
    const observer = new ResizeObserver(() => {
      if (area.clientWidth !== width || area.clientHeight !== height) {
        width = area.clientWidth;
        height = area.clientHeight;
        follow();
      }
    });
    observer.observe(area);
    // `load` does not bubble, so it is caught on its way down to the picture.
    area.addEventListener("load", follow, true);
    area.addEventListener("scroll", mark);
    return (): void => {
      observer.disconnect();
      area.removeEventListener("load", follow, true);
      area.removeEventListener("scroll", mark);
    };
  }, []);

  return areaRef;
}

function moveCursor (positionChange: number = 1) {
  // Note: the new caretPosition can equal -1 indicating that the caret is before the
  // first symbol in the `payloads` array.  But, it cannot be less than -1.
  const newPosition = clamp(changeEncodingContents.value.caretPosition + positionChange, -1, changeEncodingContents.value.payloads.length - 1);
  // Through the gate like any other edit, though the symbols do not change. A guard that
  // compares the message text has nothing to object to, so a caret move normally passes.
  editMessage({
    payloads: changeEncodingContents.value.payloads,
    caretPosition: newPosition
  });
};

export function incrementCursor () {
  moveCursor(1);
}

export function decrementCursor () {
  moveCursor(-1);
}

export function moveCursorToHome () {
  moveCursor(Number.NEGATIVE_INFINITY);
};

export function moveCursorToEnd () {
  moveCursor(Number.POSITIVE_INFINITY);
};

function handleKeyDown(event: KeyboardEvent) {
  // The browser's own arrow-key scroll would run after `useCaretInView()` and push the caret out of view.
  if ((!(isApplePlatform && event.metaKey) && event.key === "ArrowLeft") || event.key === "ArrowDown") {
    event.preventDefault();
    decrementCursor();
    announceIfEnabled(t("cursorBackward"));
  }

  if ((!(isApplePlatform && event.metaKey) && event.key === "ArrowRight") || event.key === "ArrowUp") {
    event.preventDefault();
    incrementCursor();
    announceIfEnabled(t("cursorForward"));
  }

  if (
    event.key === "Home" ||
    (event.ctrlKey && event.key === "a") ||
    (isApplePlatform && event.metaKey && event.key === "ArrowLeft")
  ) {
    event.preventDefault();
    moveCursorToHome();
    announceIfEnabled(t("cursorStart"));
  }

  if (
    event.key === "End" ||
    (event.ctrlKey && event.key === "e") ||
    (isApplePlatform && event.metaKey && event.key === "ArrowRight")
  ) {
    event.preventDefault();
    moveCursorToEnd();
    announceIfEnabled(t("cursorEnd"));
  }
}

export function ContentEncoding (props: ContentEncodingProps): VNode {
  // Read so this draws again after a save. See `settingsSavedCount`.
  void settingsSavedCount.value;
  const { id, options } = props;
  const { columnStart, columnSpan, rowStart, rowSpan } = options;
  const areaRef = useCaretInView();

  const gridStyles = generateGridStyle(columnStart, columnSpan, rowStart, rowSpan);

  return html`
    <div class="messageScroller" style="${gridStyles}">
      <div
        id="${id}"
        ref=${areaRef}
        class="contentEncodingArea"
        role="textbox"
        aria-label=${t("inputArea")}
        aria-readonly="true"
        tabindex="0"
        onKeyDown=${handleKeyDown}>
        ${messageMarkup()}
      </div>
    </div>
  `;
}
